/**
 * PrivacyPage — `/app/privacy`. THE HERO of the personal Monitor app.
 *
 * On load: GET /me/privacy/dashboard. Shows big headline numbers
 * (found · blocked · still watching), then a scrollable list of trackers —
 * who, what they accessed, how many times, a Block/Blocked button, and a
 * "see the proof" link that re-checks the public signature.
 *
 * Block → POST /me/privacy/block. On {reason:"upgrade"} we show a friendly
 * upgrade sheet. If the dashboard is empty, a "Scan now" button seeds a
 * realistic sample via POST /me/privacy/report so the screen is alive.
 *
 * Honest note: a browser can't see every tracker — live detection needs the
 * companion app, which we mark as "coming".
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mic, MapPin, Globe, Eye, ShieldCheck, Lock } from 'lucide-react';

import {
  getMonitorToken,
  fetchPrivacyDashboard,
  reportPrivacy,
  blockTracker,
  verifyResult,
  type PrivacyTracker,
  type PrivacyObservation,
  type BlockResult,
} from '@/pages/monitor/monitorClient';
import { c } from './theme';
import { CountUp } from './CountUp';
import { ErrorCard, EmptyCard } from './ui';

// A realistic seed so the very first open demonstrates the WOW immediately.
const SAMPLE: PrivacyObservation[] = [
  { tracker_name: 'Instagram', category: 'Social', accessed_what: 'your microphone', count: 23 },
  { tracker_name: 'Google', category: 'Search', accessed_what: 'your location', count: 184 },
  { tracker_name: 'TikTok', category: 'Social', accessed_what: 'your browsing', count: 57 },
  { tracker_name: 'Meta Pixel', category: 'Ads', accessed_what: 'websites you visit', count: 412 },
];

function accessIcon(accessed: string) {
  const a = accessed.toLowerCase();
  if (a.includes('mic')) return Mic;
  if (a.includes('location')) return MapPin;
  if (a.includes('brows') || a.includes('website')) return Globe;
  return Eye;
}

export function PrivacyPage() {
  const qc = useQueryClient();
  const token = getMonitorToken();
  const [upgrade, setUpgrade] = useState<BlockResult['options'] | null>(null);

  const dash = useQuery({
    queryKey: ['app', 'privacy', token],
    queryFn: () => fetchPrivacyDashboard(token!),
    enabled: Boolean(token),
  });

  const scan = useMutation({
    mutationFn: () => reportPrivacy(token!, SAMPLE),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app', 'privacy', token] }),
  });

  const block = useMutation({
    mutationFn: (name: string) => blockTracker(token!, name),
    onSuccess: (res) => {
      if (res.blocked) {
        qc.invalidateQueries({ queryKey: ['app', 'privacy', token] });
      } else if (res.reason === 'upgrade' && res.options) {
        setUpgrade(res.options);
      }
    },
  });

  const data = dash.data;
  const trackers = data?.trackers ?? [];

  return (
    <div style={{ padding: '20px 18px 8px' }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '8px 0 4px' }}>Who's watching you</h1>
      <p style={{ fontSize: 14, color: c.inkMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
        These apps reached into your phone. Tap Block to shut them out.
      </p>

      {dash.isPending && <SkeletonHeadline />}

      {dash.isError && (
        <ErrorCard onRetry={() => dash.refetch()}>
          We couldn't load your trackers just now.
        </ErrorCard>
      )}

      {!dash.isPending && !dash.isError && data && (
        <>
          <Headline detected={data.detected} blocked={data.blocked} active={data.active} />

          {trackers.length === 0 ? (
            <EmptyCard
              title="Let's see who's tracking you"
              body="Run a quick scan to reveal the apps reaching into your phone."
              actionLabel={scan.isPending ? 'Scanning…' : 'Scan now'}
              onAction={() => scan.mutate()}
              disabled={scan.isPending}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
              {trackers.map((t) => (
                <TrackerCard
                  key={t.tracker_name + t.accessed_what}
                  tracker={t}
                  blocking={block.isPending && block.variables === t.tracker_name}
                  onBlock={() => block.mutate(t.tracker_name)}
                />
              ))}
            </div>
          )}

          {scan.isError && (
            <p style={{ fontSize: 13, color: c.danger, marginTop: 12 }}>
              Scan didn't go through — tap to try again.
            </p>
          )}
        </>
      )}

      <HonestNote />

      {upgrade && <UpgradeSheet options={upgrade} onClose={() => setUpgrade(null)} />}
    </div>
  );
}

// ── Headline numbers ─────────────────────────────────────────────────────────

function Headline({
  detected,
  blocked,
  active,
}: {
  detected: number;
  blocked: number;
  active: number;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      <Stat n={detected} label="found" fg={c.ink} bg={c.canvas} rim={c.rim} />
      <Stat n={blocked} label="blocked" fg={c.good} bg={c.goodSoft} rim={c.goodRim} />
      <Stat n={active} label="still watching" fg={c.danger} bg={c.dangerSoft} rim={c.dangerRim} />
    </div>
  );
}

function Stat({
  n,
  label,
  fg,
  bg,
  rim,
}: {
  n: number;
  label: string;
  fg: string;
  bg: string;
  rim: string;
}) {
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${rim}`,
        borderRadius: 16,
        padding: '16px 10px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 32, fontWeight: 800, color: fg, lineHeight: 1 }}>
        <CountUp target={n} durationMs={900} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: c.inkMuted, marginTop: 6 }}>{label}</div>
    </div>
  );
}

function SkeletonHeadline() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{ height: 86, borderRadius: 16, background: c.canvas, border: `1px solid ${c.rim}` }}
        />
      ))}
    </div>
  );
}

// ── Tracker card ─────────────────────────────────────────────────────────────

function TrackerCard({
  tracker,
  blocking,
  onBlock,
}: {
  tracker: PrivacyTracker;
  blocking: boolean;
  onBlock: () => void;
}) {
  const Icon = accessIcon(tracker.accessed_what);
  return (
    <div
      style={{
        background: c.surface,
        border: `1px solid ${c.rim}`,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 12,
          background: tracker.blocked ? c.goodSoft : c.dangerSoft,
          color: tracker.blocked ? c.good : c.danger,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </span>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{tracker.tracker_name}</div>
        <div style={{ fontSize: 13, color: c.inkMuted, marginTop: 2 }}>
          touched {tracker.accessed_what} · {tracker.count}×
        </div>
        <ProofLink verifyUrl={tracker.verify_url} />
      </div>

      {tracker.blocked ? (
        <span
          style={{
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 700,
            color: c.good,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <ShieldCheck size={16} /> Blocked
        </span>
      ) : (
        <button
          type="button"
          onClick={onBlock}
          disabled={blocking}
          style={{
            flexShrink: 0,
            minHeight: 44,
            padding: '0 18px',
            fontSize: 14,
            fontWeight: 700,
            color: '#ffffff',
            background: blocking ? '#93a4c8' : c.accent,
            border: 'none',
            borderRadius: 12,
            cursor: blocking ? 'default' : 'pointer',
          }}
        >
          {blocking ? '…' : 'Block'}
        </button>
      )}
    </div>
  );
}

function ProofLink({ verifyUrl }: { verifyUrl: string }) {
  const [state, setState] = useState<'idle' | 'checking' | 'proven' | 'failed'>('idle');
  if (!verifyUrl) return null;

  async function run() {
    setState('checking');
    try {
      setState((await verifyResult(verifyUrl)) ? 'proven' : 'failed');
    } catch {
      setState('failed');
    }
  }

  if (state === 'proven') {
    return (
      <span style={{ fontSize: 12, fontWeight: 700, color: c.good, marginTop: 6, display: 'inline-block' }}>
        ✓ Proven — show anyone
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <button
        type="button"
        onClick={run}
        style={{
          marginTop: 6,
          fontSize: 12,
          fontWeight: 600,
          color: c.inkSubtle,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        couldn't check — retry
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={run}
      disabled={state === 'checking'}
      style={{
        marginTop: 6,
        fontSize: 12,
        fontWeight: 600,
        color: c.accent,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: state === 'checking' ? 'default' : 'pointer',
        textDecoration: 'underline',
      }}
    >
      {state === 'checking' ? 'checking…' : 'see the proof'}
    </button>
  );
}

// ── Honest companion note ────────────────────────────────────────────────────

function HonestNote() {
  return (
    <div
      style={{
        marginTop: 22,
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        background: c.accentSoft,
        border: `1px solid ${c.rim}`,
        borderRadius: 14,
        padding: '14px 16px',
      }}
    >
      <Lock size={18} style={{ color: c.accent, flexShrink: 0, marginTop: 1 }} />
      <p style={{ fontSize: 13, color: c.inkMuted, margin: 0, lineHeight: 1.5 }}>
        Live detection runs through the Monitor companion — <strong>coming to your phone soon</strong>.
        A web page can't see everything your apps do behind the scenes.
      </p>
    </div>
  );
}

// ── Upgrade sheet ────────────────────────────────────────────────────────────

function UpgradeSheet({
  options,
  onClose,
}: {
  options: NonNullable<BlockResult['options']>;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: c.surface,
          borderRadius: '20px 20px 0 0',
          padding: '24px 22px calc(24px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            background: c.rim,
            margin: '0 auto 18px',
          }}
        />
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>
          Your first block is free 🎉
        </h2>
        <p style={{ fontSize: 15, color: c.inkMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
          Want to shut all of them out? Block everything, forever, for less than a coffee.
        </p>

        <button
          type="button"
          style={{
            width: '100%',
            minHeight: 54,
            fontSize: 17,
            fontWeight: 800,
            color: '#ffffff',
            background: c.accent,
            border: 'none',
            borderRadius: 14,
            cursor: 'pointer',
            marginBottom: 12,
          }}
        >
          Block everything — ${options.unlimited_monthly_usd.toFixed(2)}/month
        </button>
        <button
          type="button"
          style={{
            width: '100%',
            minHeight: 48,
            fontSize: 15,
            fontWeight: 700,
            color: c.accent,
            background: c.surface,
            border: `1px solid ${c.rim}`,
            borderRadius: 14,
            cursor: 'pointer',
          }}
        >
          Just this one — ${options.per_block_usd.toFixed(2)}
        </button>

        <p style={{ fontSize: 12, color: c.inkSubtle, textAlign: 'center', margin: '16px 0 0' }}>
          Payments are coming soon — we'll let you know the moment they're live.
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            display: 'block',
            margin: '14px auto 0',
            fontSize: 14,
            fontWeight: 600,
            color: c.inkSubtle,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}

export default PrivacyPage;
